// The demo's actions: assign, cancel and remind work in memory for the length
// of the visit, with the same journal the real console keeps, so a design
// review can click through every flow without touching a database.
import { demoHomes } from './demo-homes';
import type { CancelReason, JournalEntry, OpsOrder, OrderStatus, Overview, PickRider } from './types';

const RIDERS: PickRider[] = [
  { id: 'r4', name: 'Jean-Paul Mballa', plate: 'CE 903 MX', rating: 4.7, can_install: false, live: true, signed_up: true, busy: null, lat: 3.8562, lng: 11.5221 },
  { id: 'r6', name: 'Alain Biyong', plate: 'CE 610 RT', rating: 4.8, can_install: true, live: true, signed_up: true, busy: null, lat: 3.841, lng: 11.49 },
  { id: 'r1', name: 'Samuel Ndzi', plate: 'CE 482 KL', rating: 4.9, can_install: true, live: true, signed_up: true, busy: { order_id: 'demo-MG-3B81F0', status: 'tech_arrived' }, lat: 3.8731, lng: 11.5368 },
  { id: 'r5', name: 'Hervé Tchoumi', plate: 'CE 256 PQ', rating: 4.6, can_install: false, live: true, signed_up: true, busy: { order_id: 'demo-MG-77D3E1', status: 'agent_en_route' }, lat: 3.8668, lng: 11.4991 },
  { id: 'r7', name: 'Patrice Zambo', plate: null, rating: null, can_install: false, live: false, signed_up: true, busy: null, lat: null, lng: null },
];

const changes = new Map<string, { status: OrderStatus; rider?: PickRider; at: string; reason?: CancelReason; note?: string }>();
const log: JournalEntry[] = [];
const listeners = new Set<() => void>();
let seq = 1;
const ping = () => setTimeout(() => listeners.forEach((f) => f()), 120);
const now = () => new Date().toISOString();

function patch(o: OpsOrder): OpsOrder {
  const c = changes.get(o.id);
  if (!c) return o;
  const n: OpsOrder = { ...o, status: c.status, events: { ...o.events, [c.status]: c.at }, log: [...o.log, { status: c.status, at: c.at, actor: 'admin' }] };
  if (c.rider) n.rider = { id: c.rider.id, name: c.rider.name, plate: c.rider.plate, rating: c.rider.rating, phone: null };
  if (c.reason) { n.failure_reason = c.reason; n.failure_note = c.note ?? null; }
  return n;
}

export const demoActions = {
  apply: (os: OpsOrder[]) => os.map(patch),
  overview(ov: Overview): Overview {
    const alerts = ov.alerts.filter((a) => !(('order' in a) && changes.has(a.order.id)));
    return { ...ov, alerts, soon_empty: ov.soon_empty.map((h) => (h.open_order ? { ...h, open_order: patch(h.open_order) } : h)) };
  },
  riders: (order: string) => RIDERS.filter((r) => r.busy?.order_id !== order),
  async assign(order: string, rider: string) {
    const r = RIDERS.find((x) => x.id === rider);
    if (!r) throw new Error('no_rider');
    if (r.busy) throw Object.assign(new Error('rider_busy'), { code: 'PT409' });
    if (!r.live) throw Object.assign(new Error('not_live'), { code: 'PT409' });
    changes.set(order, { status: 'assigned', rider: r, at: now() });
    log.unshift({ id: seq++, at: now(), actor: 'Ruben Bisso', action: 'assign', order_id: order, ref: order.replace('demo-', ''), customer: null, detail: { rider: r.name } });
    ping();
    return { id: order } as OpsOrder;
  },
  async cancel(order: string, reason: CancelReason, note?: string) {
    changes.set(order, { status: 'cancelled_by_agency', at: now(), reason, note });
    log.unshift({ id: seq++, at: now(), actor: 'Ruben Bisso', action: 'cancel', order_id: order, ref: order.replace('demo-', ''), customer: null, detail: { reason, note: note ?? null } });
    ping();
    return { id: order } as OpsOrder;
  },
  async remind(customers: string[]) {
    const homes = demoHomes();
    customers.forEach((c) => {
      const h = homes.find((x) => x.customer_id === c);
      log.unshift({ id: seq++, at: now(), actor: 'Ruben Bisso', action: 'remind', order_id: null, ref: null, customer: h?.name ?? c, detail: { push: 'queued', level: h?.home?.level ?? undefined } });
    });
    ping();
    return { sent: customers.length, skipped: 0 };
  },
  journal: async () => log.slice(0, 100),
  listen(f: () => void) { listeners.add(f); return () => { listeners.delete(f); }; },
};
